import { ref, computed, watch, type Ref } from 'vue';
import { aiService } from '@/services/AIService';
import type { FieldOverride, ReviewEditInput, ReviewLineOpInput, LineFieldInput } from '@/types/ai';

// Unsaved reviewer edit: a corrected value, an exclusion flag, or both. `reverted` marks a
// field whose prior saved override the reviewer has undone — it folds to a cleared field
// (no value, not excluded) when the review is submitted.
interface PendingLocalChange {
  value?: string | number | boolean;
  excluded: boolean;
  reverted?: boolean;
}

// Unsaved structural coverage-line operation, keyed by the line it targets. One net op per
// line: recategorize-then-delete folds to delete; deleting a line added this session drops
// the add entirely. `add` ops carry the line's field values (they are submitted inside the
// add operation, never as ordinary field edits).
export interface PendingLineOp {
  op: 'add' | 'delete' | 'recategorize';
  // Server lineKey ("raw-<N>" / "add-<hex>") or a session-local "new-<n>" key for lines
  // added this session (the server assigns the real key on submit).
  lineKey: string;
  coverageTypeId?: number;
  coverageTypeName?: string;
  fields?: Map<string, string | number | boolean | undefined>;
}

const isNewLineKey = (lineKey: string): boolean => lineKey.startsWith('new-');

export function useFieldOverrides(promptResultId: Ref<number | null>) {
  // State
  const overrides = ref<Map<string, FieldOverride>>(new Map());
  const pendingLocalChanges = ref<Map<string, PendingLocalChange>>(new Map());
  const pendingLineOps = ref<Map<string, PendingLineOp>>(new Map());
  const pendingSaves = ref<Set<string>>(new Set());
  const saveErrors = ref<Map<string, string>>(new Map());
  const isLoading = ref(false);
  const isReviewMode = ref(false);
  const isSavingAll = ref(false);
  let newLineCounter = 0;

  // Computed
  const overrideCount = computed(() => overrides.value.size);
  const overriddenFieldPaths = computed(() => Array.from(overrides.value.keys()));
  const hasPendingChanges = computed(
    () => pendingLocalChanges.value.size > 0 || pendingLineOps.value.size > 0
  );
  const pendingChangeCount = computed(() => pendingLocalChanges.value.size + pendingLineOps.value.size);
  const pendingAddedLines = computed(() =>
    Array.from(pendingLineOps.value.values()).filter((op) => op.op === 'add')
  );

  // Watch for promptResultId changes to reset state
  watch(promptResultId, () => {
    overrides.value.clear();
    pendingLocalChanges.value.clear();
    pendingLineOps.value.clear();
    pendingSaves.value.clear();
    saveErrors.value.clear();
    isReviewMode.value = false;
  });

  // Methods
  const loadOverrides = async (): Promise<void> => {
    if (!promptResultId.value) return;

    isLoading.value = true;
    try {
      const response = await aiService.getFieldOverrides(promptResultId.value);
      overrides.value.clear();
      response.overrides.forEach((override) => {
        overrides.value.set(override.fieldPath, override);
      });
    } catch (error) {
      console.error('Failed to load overrides:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const getOverride = (fieldPath: string): FieldOverride | undefined => {
    return overrides.value.get(fieldPath);
  };

  const hasOverride = (fieldPath: string): boolean => {
    return overrides.value.has(fieldPath);
  };

  const isPending = (fieldPath: string): boolean => {
    return pendingSaves.value.has(fieldPath);
  };

  const getError = (fieldPath: string): string | undefined => {
    return saveErrors.value.get(fieldPath);
  };

  const clearError = (fieldPath: string): void => {
    saveErrors.value.delete(fieldPath);
  };

  // Local change tracking (deferred save)
  const setLocalOverride = (fieldPath: string, value: string | number | boolean): void => {
    const existing = pendingLocalChanges.value.get(fieldPath);
    pendingLocalChanges.value.set(fieldPath, {
      value,
      excluded: existing?.excluded ?? false,
    });
    saveErrors.value.delete(fieldPath);
  };

  const revertLocalOverride = (fieldPath: string): void => {
    pendingLocalChanges.value.delete(fieldPath);
    saveErrors.value.delete(fieldPath);
  };

  // Mark a field whose prior saved override should be undone. Tracked as a pending change so the
  // field reads back as not-overridden and, on submit, emits a revert edit that clears it.
  const setLocalRevert = (fieldPath: string): void => {
    pendingLocalChanges.value.set(fieldPath, { value: undefined, excluded: false, reverted: true });
    saveErrors.value.delete(fieldPath);
  };

  const isReverted = (fieldPath: string): boolean => {
    return pendingLocalChanges.value.get(fieldPath)?.reverted === true;
  };

  const hasLocalChange = (fieldPath: string): boolean => {
    return pendingLocalChanges.value.has(fieldPath);
  };

  const getLocalChange = (fieldPath: string): string | number | boolean | undefined => {
    return pendingLocalChanges.value.get(fieldPath)?.value;
  };

  const setLocalExclusion = (fieldPath: string, excluded: boolean): void => {
    const existing = pendingLocalChanges.value.get(fieldPath);
    const saved = overrides.value.get(fieldPath);
    const savedExcluded = saved?.isExcluded ?? false;

    if (excluded === savedExcluded && existing?.value === undefined) {
      pendingLocalChanges.value.delete(fieldPath);
      saveErrors.value.delete(fieldPath);
      return;
    }

    pendingLocalChanges.value.set(fieldPath, {
      value: existing?.value,
      excluded,
    });
    saveErrors.value.delete(fieldPath);
  };

  const hasLocalExclusionChange = (fieldPath: string): boolean => {
    const pending = pendingLocalChanges.value.get(fieldPath);
    if (!pending) return false;
    const saved = overrides.value.get(fieldPath);
    return pending.excluded !== (saved?.isExcluded ?? false);
  };

  const isExcluded = (fieldPath: string): boolean => {
    const pending = pendingLocalChanges.value.get(fieldPath);
    if (pending) return pending.excluded;
    return overrides.value.get(fieldPath)?.isExcluded ?? false;
  };

  const clearPendingChanges = (): void => {
    pendingLocalChanges.value.clear();
    pendingLineOps.value.clear();
  };

  // ---- Structural coverage-line operations (submitted with the review as lineOps) ----

  // Stage a new coverage line. Field values start empty and are filled via
  // setPendingLineField. Returns the session-local line key used to address it.
  const addPendingLine = (
    coverageTypeId: number,
    coverageTypeName: string,
    fieldNames: string[]
  ): string => {
    const lineKey = `new-${++newLineCounter}`;
    const fields = new Map<string, string | number | boolean | undefined>();
    fieldNames.forEach((name) => fields.set(name, undefined));
    pendingLineOps.value.set(lineKey, { op: 'add', lineKey, coverageTypeId, coverageTypeName, fields });
    return lineKey;
  };

  // Route a field edit into an added line's op. Works for lines added this session and for
  // previously-added ("add-*") lines with a staged superseding op (see ensureSupersedingAdd).
  const setPendingLineField = (
    lineKey: string,
    fieldName: string,
    value: string | number | boolean | undefined
  ): void => {
    const op = pendingLineOps.value.get(lineKey);
    if (!op || op.op !== 'add') return;
    op.fields = op.fields ?? new Map();
    op.fields.set(fieldName, value);
    // Reassign to keep Map reactivity dependable across Vue versions.
    pendingLineOps.value.set(lineKey, { ...op });
  };

  const getPendingLineField = (
    lineKey: string,
    fieldName: string
  ): string | number | boolean | undefined => {
    return pendingLineOps.value.get(lineKey)?.fields?.get(fieldName);
  };

  // Editing a previously-added line (server "add-*" key) in a later review supersedes the
  // whole add: stage an add op seeded from the line's current merged values, then edit
  // fields through setPendingLineField. The backend rejects ordinary field edits on added
  // lines, so their values must always travel inside the add operation.
  const ensureSupersedingAdd = (
    lineKey: string,
    coverageTypeId: number | undefined,
    coverageTypeName: string | undefined,
    seedFields: Array<{ name: string; value: string | number | boolean | undefined }>
  ): void => {
    if (pendingLineOps.value.has(lineKey)) return;
    const fields = new Map<string, string | number | boolean | undefined>();
    seedFields.forEach((f) => fields.set(f.name, f.value));
    pendingLineOps.value.set(lineKey, { op: 'add', lineKey, coverageTypeId, coverageTypeName, fields });
  };

  // Mark a line deleted. Deleting a line added this session (or a staged superseding add)
  // simply drops the pending add. pathPrefix ("coverageLines.<idx>.") clears any pending
  // field edits under the line so they are not submitted against it.
  const markLineDeleted = (lineKey: string, pathPrefix?: string): void => {
    const existing = pendingLineOps.value.get(lineKey);
    if (existing?.op === 'add' && isNewLineKey(lineKey)) {
      pendingLineOps.value.delete(lineKey);
      return;
    }
    pendingLineOps.value.set(lineKey, { op: 'delete', lineKey });
    if (pathPrefix) {
      for (const fieldPath of Array.from(pendingLocalChanges.value.keys())) {
        if (fieldPath.startsWith(pathPrefix)) {
          pendingLocalChanges.value.delete(fieldPath);
          saveErrors.value.delete(fieldPath);
        }
      }
    }
  };

  // Undo a delete staged this session.
  const unmarkLineDeleted = (lineKey: string): void => {
    if (pendingLineOps.value.get(lineKey)?.op === 'delete') {
      pendingLineOps.value.delete(lineKey);
    }
  };

  const isLineDeleted = (lineKey: string): boolean => {
    return pendingLineOps.value.get(lineKey)?.op === 'delete';
  };

  // Stage a recategorization. Recategorizing a staged add just retargets its type.
  const setLineRecategorize = (
    lineKey: string,
    coverageTypeId: number,
    coverageTypeName: string
  ): void => {
    const existing = pendingLineOps.value.get(lineKey);
    if (existing?.op === 'add') {
      pendingLineOps.value.set(lineKey, { ...existing, coverageTypeId, coverageTypeName });
      return;
    }
    pendingLineOps.value.set(lineKey, { op: 'recategorize', lineKey, coverageTypeId, coverageTypeName });
  };

  const clearLineRecategorize = (lineKey: string): void => {
    if (pendingLineOps.value.get(lineKey)?.op === 'recategorize') {
      pendingLineOps.value.delete(lineKey);
    }
  };

  const getLineRecategorize = (lineKey: string): PendingLineOp | undefined => {
    const op = pendingLineOps.value.get(lineKey);
    return op?.op === 'recategorize' ? op : undefined;
  };

  const getPendingLineOp = (lineKey: string): PendingLineOp | undefined => {
    return pendingLineOps.value.get(lineKey);
  };

  // buildLineOpsPayload maps the net per-line ops to the request shape. New lines omit
  // lineKey (the server assigns "add-<hex>"); superseding adds keep their server key.
  // Undefined field values are dropped — a field left blank is simply not captured.
  const buildLineOpsPayload = (): ReviewLineOpInput[] => {
    const lineOps: ReviewLineOpInput[] = [];
    for (const op of pendingLineOps.value.values()) {
      if (op.op === 'add') {
        const fields: LineFieldInput[] = [];
        op.fields?.forEach((value, name) => {
          if (value !== undefined && value !== '') {
            fields.push({ name, value });
          }
        });
        lineOps.push({
          operation: 'add',
          lineKey: isNewLineKey(op.lineKey) ? undefined : op.lineKey,
          coverageTypeId: op.coverageTypeId,
          fields,
        });
      } else if (op.op === 'delete') {
        lineOps.push({ operation: 'delete', lineKey: op.lineKey });
      } else {
        lineOps.push({ operation: 'recategorize', lineKey: op.lineKey, coverageTypeId: op.coverageTypeId });
      }
    }
    return lineOps;
  };

  // buildEditPayload maps the reviewer's pending local changes to the edit set submitted when
  // a review completes. Reviewer edits are never persisted before that point.
  //
  // Reviews stack: the loaded `overrides` reflect the resolved state of prior reviews. An edit
  // is emitted for a corrected value or an exclusion. When the user reverts a field back to its
  // original value, an edit restoring that original is emitted only if a prior override exists
  // (so the fold undoes it); reverting to a null original cannot be expressed and is skipped.
  const buildEditPayload = (): ReviewEditInput[] => {
    const edits: ReviewEditInput[] = [];

    for (const [fieldPath, change] of pendingLocalChanges.value.entries()) {
      const existingOverride = overrides.value.get(fieldPath);
      const hasValueEdit = change.value !== undefined;
      const valueMatchesOriginal =
        hasValueEdit && existingOverride != null && existingOverride.originalValue === change.value;

      // Explicit revert: emit a clearing edit only when there is a prior override to undo.
      if (change.reverted) {
        const hasPriorOverride =
          existingOverride != null && (existingOverride.overrideValue !== null || existingOverride.isExcluded);
        if (hasPriorOverride) {
          edits.push({ fieldPath, isReverted: true });
        }
        continue;
      }

      if (change.excluded) {
        const e: ReviewEditInput = { fieldPath, isExcluded: true };
        if (hasValueEdit && !valueMatchesOriginal) {
          e.value = change.value;
        }
        edits.push(e);
        continue;
      }

      if (hasValueEdit && !valueMatchesOriginal) {
        edits.push({ fieldPath, value: change.value });
        continue;
      }

      // Revert/accept: only emit when there is a prior override to undo and a non-null original
      // to restore. Otherwise this is a no-op accepted field.
      const hasPriorOverride =
        existingOverride != null && (existingOverride.overrideValue !== null || existingOverride.isExcluded);
      if (hasPriorOverride && existingOverride!.originalValue !== null) {
        edits.push({ fieldPath, value: existingOverride!.originalValue });
      }
    }

    return edits;
  };

  const enterReviewMode = async (): Promise<void> => {
    await loadOverrides();
    isReviewMode.value = true;
  };

  const exitReviewMode = (): void => {
    isReviewMode.value = false;
    // Drop review-scoped state so it cannot leak into the read-only display (where values come
    // from props) or into the next review session. The loaded overrides are stale once a review
    // is submitted; enterReviewMode reloads them.
    overrides.value.clear();
    pendingLocalChanges.value.clear();
    pendingLineOps.value.clear();
    pendingSaves.value.clear();
    saveErrors.value.clear();
  };

  const toggleReviewMode = async (): Promise<void> => {
    if (isReviewMode.value) {
      exitReviewMode();
    } else {
      await enterReviewMode();
    }
  };

  return {
    // State
    overrides,
    isLoading,
    isReviewMode,
    isSavingAll,
    overrideCount,
    overriddenFieldPaths,
    hasPendingChanges,
    pendingChangeCount,

    // Methods
    loadOverrides,
    getOverride,
    hasOverride,
    isPending,
    getError,
    clearError,
    enterReviewMode,
    exitReviewMode,
    toggleReviewMode,

    // Local change tracking (persisted only at review completion)
    setLocalOverride,
    revertLocalOverride,
    setLocalRevert,
    isReverted,
    hasLocalChange,
    getLocalChange,
    setLocalExclusion,
    hasLocalExclusionChange,
    isExcluded,
    clearPendingChanges,
    buildEditPayload,

    // Structural coverage-line operations (persisted only at review completion)
    pendingLineOps,
    pendingAddedLines,
    addPendingLine,
    setPendingLineField,
    getPendingLineField,
    ensureSupersedingAdd,
    markLineDeleted,
    unmarkLineDeleted,
    isLineDeleted,
    setLineRecategorize,
    clearLineRecategorize,
    getLineRecategorize,
    getPendingLineOp,
    buildLineOpsPayload,
  };
}
